import { Color } from 'three';

import {
    formatLength,
    formatSizeVariants
} from 'app/modules/wallforming/components/scenes/objects/dimensions/core/dimensionFormat';
import {
    rectContains,
    rectIntersectsRing,
    rectsOverlap,
    type Polygon2D
} from 'app/modules/wallforming/components/scenes/objects/dimensions/core/geometry2d';
import {
    emptyRect,
    extendRect,
    isRectValid,
    mapPoint,
    mapRect,
    unionRect,
    unmapLengthY,
    type DisplayMapping,
    type Rect,
    type Vec2
} from 'app/modules/wallforming/components/scenes/objects/dimensions/core/viewFrame';
import { DIMENSION_COLOR } from 'shared/constants';

import type { DimensionScene } from 'app/modules/wallforming/components/scenes/objects/dimensions/core/dimensionScene';
import type { DimensionSettings } from 'app/modules/wallforming/components/scenes/objects/dimensions/types/dimensionSettings';

export interface DrawingFill {
    polygon: Polygon2D;
    color: string;
    opacity: number;
}

export interface DrawingLine {
    a: Vec2;
    b: Vec2;
    color: string;
}

export interface DrawingText {
    text: string;
    /** centre of the text line in display coordinates */
    position: Vec2;
    size: number;
    /** 0 = horizontal, 90 = rotated counter-clockwise (reads from the right) */
    rotation: 0 | 90;
    color: string;
}

/** Flat 2D drawing in display coordinates; fills are ordered back to front. */
export interface DimensionDrawing {
    fills: DrawingFill[];
    lines: DrawingLine[];
    dashedLines: DrawingLine[];
    texts: DrawingText[];
}

export interface LayoutOptions {
    settings: DimensionSettings;
    groupColors: Record<string, string>;
    /** text width for a font size of 1 */
    measureText: (text: string) => number;
}

const OUTLINE_COLOR = '#1a1b1e';
const PANEL_COLOR = '#ffffff';
const ACCESSORY_COLOR = '#5c5f66';
const BACKGROUND_COLOR = '#ffffff';
/** backdrop padding around the drawing, relative to the text height */
const BACKGROUND_PADDING = 1.5;
/** text height relative to the wall width, clamped to a readable range (metres) */
const TEXT_SIZE_RATIO = 1 / 80;
const MIN_TEXT_SIZE = 0.03;
const MAX_TEXT_SIZE = 0.09;
const MERGE_TOLERANCE = 1e-3;
/** line pitch of multi-line texts relative to the font size (matches the text atlas) */
const LINE_SPACING = 72 / 64;

// ISO 129 proportions relative to the text height h
const EXTENSION_GAP = 0.3;
const EXTENSION_OVERSHOOT = 0.25;
const TICK_HALF = 0.3;
const TEXT_GAP = 0.3;
const TEXT_ROW = 1.15;
const FIRST_TRACK = 1.8;
const TRACK_SPACING = 3;
/** free space between the outermost value of a chain and the next dimension line */
const TRACK_CLEARANCE = 1.2;
/** minimum distance between two texts */
const TEXT_MARGIN = 0.3;
/** lines are interrupted around texts with this clearance */
const LINE_CLEARANCE = 0.1;

const LABEL_PADDING = 0.15;
const LABEL_SCALES = [1, 0.85, 0.7, 0.55];
const ACCESSORY_LABEL_SCALE = 0.4;
const MAX_LABEL_CANDIDATES = 120;
const CHAIN_SCALES = [1, 0.85, 0.7, 0.6];
const CHAIN_MAX_ROWS = 8;

interface Obstacle {
    bounds: Rect;
    ring: Vec2[];
}

const darken = (hex: string, factor = 0.6) => `#${new Color(hex).multiplyScalar(factor).getHexString()}`;

const rectPolygon = (r: Rect): Polygon2D => [
    [
        { x: r.minX, y: r.minY },
        { x: r.maxX, y: r.minY },
        { x: r.maxX, y: r.maxY },
        { x: r.minX, y: r.maxY }
    ]
];

const padRect = (r: Rect, pad: number): Rect => ({
    minX: r.minX - pad,
    minY: r.minY - pad,
    maxX: r.maxX + pad,
    maxY: r.maxY + pad
});

function uniqueStops(values: number[]): number[] {
    const sorted = [...values].filter(Number.isFinite).sort((a, b) => a - b);
    const result: number[] = [];
    for (const v of sorted) {
        if (result.length === 0 || v - result[result.length - 1] > MERGE_TOLERANCE) {
            result.push(v);
        }
    }
    return result;
}

const sameStops = (a: number[], b: number[]) =>
    a.length === b.length && a.every((v, i) => Math.abs(v - b[i]) <= MERGE_TOLERANCE);

/** Parameter interval [t0, t1] of the segment aâ†’b inside the rect (Liangâ€“Barsky), or null. */
function clipInterval(a: Vec2, b: Vec2, r: Rect): [number, number] | null {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const p = [-dx, dx, -dy, dy];
    const q = [a.x - r.minX, r.maxX - a.x, a.y - r.minY, r.maxY - a.y];
    let t0 = 0;
    let t1 = 1;
    for (let k = 0; k < 4; k++) {
        if (Math.abs(p[k]) < 1e-12) {
            if (q[k] < 0) {
                return null;
            }
            continue;
        }
        const t = q[k] / p[k];
        if (p[k] < 0) {
            t0 = Math.max(t0, t);
        } else {
            t1 = Math.min(t1, t);
        }
        if (t0 >= t1) {
            return null;
        }
    }
    return [t0, t1];
}

/** Removes the parts of the line that run through any of the rects. */
function subtractRects(line: DrawingLine, rects: Rect[]): DrawingLine[] {
    const segmentBounds = extendRect(extendRect(emptyRect(), line.a), line.b);
    const cuts = rects
        .filter((r) => rectsOverlap(r, segmentBounds))
        .map((r) => clipInterval(line.a, line.b, r))
        .filter((c): c is [number, number] => c !== null)
        .sort((x, y) => x[0] - y[0]);
    if (cuts.length === 0) {
        return [line];
    }

    const lerp = (t: number): Vec2 => ({ x: line.a.x + (line.b.x - line.a.x) * t, y: line.a.y + (line.b.y - line.a.y) * t });
    const pieces: DrawingLine[] = [];
    let start = 0;
    for (const [t0, t1] of cuts) {
        if (t0 > start + 1e-6) {
            pieces.push({ a: lerp(start), b: lerp(t0), color: line.color });
        }
        start = Math.max(start, t1);
    }
    if (start < 1 - 1e-6) {
        pieces.push({ a: lerp(start), b: line.b, color: line.color });
    }
    return pieces;
}

class DrawingBuilder {
    readonly drawing: DimensionDrawing = { fills: [], lines: [], dashedLines: [], texts: [] };
    private readonly textRects: Rect[] = [];

    constructor(
        readonly h: number,
        readonly measure: (text: string) => number
    ) {}

    /** Bounding box of a (multi-line) text block centred at `center`. */
    textBox(lines: string[], size: number, center: Vec2, rotation: 0 | 90): Rect {
        const along = Math.max(...lines.map((l) => this.measure(l))) * size;
        const across = size * (1 + (lines.length - 1) * LINE_SPACING);
        const halfW = (rotation === 0 ? along : across) / 2;
        const halfH = (rotation === 0 ? across : along) / 2;
        return { minX: center.x - halfW, maxX: center.x + halfW, minY: center.y - halfH, maxY: center.y + halfH };
    }

    isFree(rect: Rect): boolean {
        return !this.textRects.some((r) => rectsOverlap(r, rect, this.h * TEXT_MARGIN));
    }

    /** True if the (axis aligned) segment does not run through any text. */
    isSegmentFree(a: Vec2, b: Vec2): boolean {
        const rect = extendRect(extendRect(emptyRect(), a), b);
        return !this.textRects.some((r) => rectsOverlap(r, rect, this.h * LINE_CLEARANCE));
    }

    addText(lines: string[], size: number, center: Vec2, rotation: 0 | 90, color = DIMENSION_COLOR) {
        this.textRects.push(this.textBox(lines, size, center, rotation));
        lines.forEach((text, i) => {
            // offset along the text's "up" direction (+y for horizontal, -x for rotated text)
            const k = ((lines.length - 1) / 2 - i) * size * LINE_SPACING;
            const position = rotation === 0 ? { x: center.x, y: center.y + k } : { x: center.x - k, y: center.y };
            this.drawing.texts.push({ text, position, size, rotation, color });
        });
    }

    /** Places the text at the first free candidate; texts are never placed on top of each other. */
    tryPlace(
        lines: string[],
        size: number,
        candidates: Vec2[],
        rotation: 0 | 90,
        accept: (box: Rect) => boolean = () => true
    ): Vec2 | null {
        for (const candidate of candidates) {
            const box = this.textBox(lines, size, candidate, rotation);
            if (this.isFree(box) && accept(box)) {
                this.addText(lines, size, candidate, rotation);
                return candidate;
            }
        }
        return null;
    }

    line(a: Vec2, b: Vec2, color: string = DIMENSION_COLOR) {
        this.drawing.lines.push({ a, b, color });
    }

    outline(polygon: Polygon2D, color: string) {
        for (const ring of polygon) {
            for (let i = 0; i < ring.length; i++) {
                this.line(ring[i], ring[(i + 1) % ring.length], color);
            }
        }
    }

    /** Interrupts all lines that cross a text (e.g. extension lines of outer chains running through inner values). */
    finish(withBackground: boolean): DimensionDrawing {
        const masks = this.textRects.map((r) => padRect(r, this.h * LINE_CLEARANCE));
        this.drawing.lines = this.drawing.lines.flatMap((l) => subtractRects(l, masks));
        this.drawing.dashedLines = this.drawing.dashedLines.flatMap((l) => subtractRects(l, masks));
        if (withBackground) {
            this.addBackground();
        }
        return this.drawing;
    }

    /** Opaque backdrop behind the whole drawing so it stays readable in front of the model. */
    private addBackground() {
        let extent = emptyRect();
        this.drawing.fills.forEach((f) => f.polygon.forEach((ring) => ring.forEach((p) => (extent = extendRect(extent, p)))));
        [...this.drawing.lines, ...this.drawing.dashedLines].forEach((l) => {
            extent = extendRect(extendRect(extent, l.a), l.b);
        });
        this.textRects.forEach((r) => (extent = unionRect(extent, r)));
        if (!isRectValid(extent)) {
            return;
        }

        const { minX, minY, maxX, maxY } = padRect(extent, this.h * BACKGROUND_PADDING);
        const ring = [
            { x: minX, y: minY },
            { x: maxX, y: minY },
            { x: maxX, y: maxY },
            { x: minX, y: maxY }
        ];
        this.drawing.fills.unshift({ polygon: [ring], color: BACKGROUND_COLOR, opacity: 1 });
    }
}

interface ChainSpec {
    axis: 'x' | 'y';
    /** stop coordinates along the axis (display) */
    stops: number[];
    /** coordinate of the dimension line across the axis */
    track: number;
    /** coordinate of the drawing edge the extension lines start from */
    edge: number;
    /** direction pointing away from the drawing */
    dir: 1 | -1;
    toReal: (displayLength: number) => number;
    format: (realLength: number) => string;
}

interface ChainCandidate {
    center: Vec2;
    size: number;
    rotation: 0 | 90;
    /** leader from the dimension line to the text, drawn when the text is detached from its segment */
    leader?: [Vec2, Vec2];
    /** dimension line extension below a value that sits outside the chain (ISO 129 for small end segments) */
    extension?: [Vec2, Vec2];
}

/**
 * Chain dimensioning according to ISO 129-1: extension lines, oblique ticks and values above the line.
 *
 * Values are placed in two passes so that every value that fits between its ticks keeps its natural position.
 * The remaining (narrow) segments try both sides of the line at every text size before moving
 * outside next to the chain end → turned perpendicular inside the segment →
 * staggered rows with a leader line. Returns the outward extent used from the track.
 */
function addChain(builder: DrawingBuilder, spec: ChainSpec): number {
    const { axis, stops, track, edge, dir, toReal, format } = spec;
    const h = builder.h;
    let extent = EXTENSION_OVERSHOOT * h;
    if (stops.length < 2) {
        return extent;
    }

    const P = (along: number, across: number): Vec2 => (axis === 'x' ? { x: along, y: across } : { x: across, y: along });
    const parallel: 0 | 90 = axis === 'x' ? 0 : 90;
    const perpendicular: 0 | 90 = axis === 'x' ? 90 : 0;
    // text "up" direction: +y for horizontal text, -x for text rotated by 90°
    const aboveSign = axis === 'x' ? 1 : -1;
    const gap = TEXT_GAP * h;

    builder.line(P(stops[0], track), P(stops[stops.length - 1], track));
    for (const s of stops) {
        builder.line(P(s, edge + dir * EXTENSION_GAP * h), P(s, track + dir * EXTENSION_OVERSHOOT * h));
        builder.line(P(s - TICK_HALF * h, track - TICK_HALF * h), P(s + TICK_HALF * h, track + TICK_HALF * h));
    }

    const place = (text: string, candidate: ChainCandidate): boolean => {
        const { center, size, rotation, leader, extension } = candidate;
        if (leader && !builder.isSegmentFree(leader[0], leader[1])) {
            return false;
        }
        if (!builder.tryPlace([text], size, [center], rotation)) {
            return false;
        }
        if (leader) {
            builder.line(leader[0], leader[1]);
        }
        if (extension) {
            builder.line(extension[0], extension[1]);
        }
        const box = builder.textBox([text], size, center, rotation);
        const far = axis === 'x' ? (dir > 0 ? box.maxY : box.minY) : dir > 0 ? box.maxX : box.minX;
        extent = Math.max(extent, (far - track) * dir);
        return true;
    };

    const segments = stops.slice(0, -1).map((start, i) => {
        const end = stops[i + 1];
        return { index: i, start, end, mid: (start + end) / 2, text: format(toReal(end - start)) };
    });

    // pass 1: values that fit between their ticks, slightly smaller rather than displaced
    const pending = segments.filter(({ start, end, mid, text }) => {
        for (const scale of CHAIN_SCALES) {
            const size = h * scale;
            const length = builder.measure(text) * size;
            if (length + 2 * TICK_HALF * h > end - start) {
                continue;
            }
            if ([aboveSign, -aboveSign].some((side) =>
                place(text, { center: P(mid, track + side * (gap + size / 2)), size, rotation: parallel })
            )) {
                return false;
            }
        }
        return true;
    });

    // pass 2: narrow segments, smallest first (they have the fewest options)
    pending.sort((a, b) => a.end - a.start - (b.end - b.start));
    for (const { index, start, end, mid, text } of pending) {
        const length = end - start;
        const isFirst = index === 0;
        const isLast = index === segments.length - 1;

        let placed = false;
        for (const scale of CHAIN_SCALES) {
            const size = h * scale;
            if ([aboveSign, -aboveSign].some((side) => place(text, {
                center: P(mid, track + side * (gap + size / 2)),
                size,
                rotation: parallel,
                leader: builder.measure(text) * size > length
                    ? [P(mid, track), P(mid, track + side * gap)]
                    : undefined
            }))) {
                placed = true;
                break;
            }
        }

        const candidates = (size: number): ChainCandidate[] => {
            const textLength = builder.measure(text) * size;
            const inline = track + aboveSign * (gap + size / 2);
            const list: ChainCandidate[] = [];

            // outside the chain, on the extended dimension line
            const outside = TICK_HALF * h + gap + textLength / 2;
            if (isLast) {
                const along = end + outside;
                list.push({ center: P(along, inline), size, rotation: parallel, extension: [P(end, track), P(along + textLength / 2, track)] });
            }
            if (isFirst) {
                const along = start - outside;
                list.push({ center: P(along, inline), size, rotation: parallel, extension: [P(start, track), P(along - textLength / 2, track)] });
            }

            // turned perpendicular inside the segment
            if (size * (1 + TEXT_MARGIN) <= length) {
                list.push({ center: P(mid, track + dir * (gap + textLength / 2)), size, rotation: perpendicular });
            }
            return list;
        };

        for (const scale of CHAIN_SCALES) {
            if (placed) {
                break;
            }
            if (candidates(h * scale).some((candidate) => place(text, candidate))) {
                placed = true;
                break;
            }
        }

        // last resort: staggered rows with a leader line back to the segment
        for (let row = 1; !placed && row <= CHAIN_MAX_ROWS; row++) {
            for (const scale of CHAIN_SCALES) {
                const size = h * scale;
                const near = track + dir * (gap + row * TEXT_ROW * size);
                const candidate: ChainCandidate = {
                    center: P(mid, near + (dir * size) / 2),
                    size,
                    rotation: parallel,
                    leader: [P(mid, track), P(mid, near - dir * gap * 0.3)]
                };
                if (place(text, candidate)) {
                    placed = true;
                    break;
                }
            }
        }
    }
    return extent;
}

/** Places the panel size inside the panel: moved, wrapped, rotated and shrunk until it is free of accessories and texts. */
function placePanelLabel(builder: DrawingBuilder, variants: string[][], rect: Rect, obstacles: Obstacle[]): boolean {
    const pad = builder.h * LABEL_PADDING;
    const inner: Rect = { minX: rect.minX + pad, maxX: rect.maxX - pad, minY: rect.minY + pad, maxY: rect.maxY - pad };
    if (!isRectValid(inner)) {
        return false;
    }

    const relevant = obstacles.filter((o) => rectsOverlap(o.bounds, rect));
    const accept = (box: Rect) =>
        rectContains(inner, box) && !relevant.some((o) => rectsOverlap(o.bounds, box) && rectIntersectsRing(box, o.ring));
    const cx = (inner.minX + inner.maxX) / 2;
    const cy = (inner.minY + inner.maxY) / 2;

    // all size / wrapping / rotation variants that fit into the panel, in order of preference
    const configs: { lines: string[]; size: number; rotation: 0 | 90; rangeX: number; rangeY: number }[] = [];
    for (const scale of LABEL_SCALES) {
        const size = builder.h * scale;
        for (const lines of variants) {
            for (const rotation of [0, 90] as const) {
                const probe = builder.textBox(lines, size, { x: 0, y: 0 }, rotation);
                const rangeX = (inner.maxX - inner.minX - (probe.maxX - probe.minX)) / 2;
                const rangeY = (inner.maxY - inner.minY - (probe.maxY - probe.minY)) / 2;
                if (rangeX >= 0 && rangeY >= 0) {
                    configs.push({ lines, size, rotation, rangeX, rangeY });
                }
            }
        }
    }

    // 1. shrink / wrap / rotate in the centre of the panel
    for (const { lines, size, rotation } of configs) {
        if (builder.tryPlace(lines, size, [{ x: cx, y: cy }], rotation, accept)) {
            return true;
        }
    }

    // 2. move away from the centre, nearest positions first
    for (const { lines, size, rotation, rangeX, rangeY } of configs) {
        const step = size * 0.5;
        const offsetsX = [0];
        const offsetsY = [0];
        for (let d = step; d <= rangeX; d += step) {
            offsetsX.push(d, -d);
        }
        for (let d = step; d <= rangeY; d += step) {
            offsetsY.push(d, -d);
        }
        const candidates = offsetsY
            .flatMap((dy) => offsetsX.map((dx) => ({ x: cx + dx, y: cy + dy })))
            .sort((a, b) => Math.hypot(a.x - cx, a.y - cy) - Math.hypot(b.x - cx, b.y - cy))
            .slice(1, MAX_LABEL_CANDIDATES);

        if (builder.tryPlace(lines, size, candidates, rotation, accept)) {
            return true;
        }
    }
    return false;
}

function placeAccessoryLabel(builder: DrawingBuilder, variants: string[][], rect: Rect, obstacles: Obstacle[]): void {
    const cx = (rect.minX + rect.maxX) / 2;
    const cy = (rect.minY + rect.maxY) / 2;
    const accept = (box: Rect) =>
        !obstacles.some((o) => rectsOverlap(o.bounds, box) && rectIntersectsRing(box, o.ring));

    for (let row = 1; row <= CHAIN_MAX_ROWS; row++) {
        // Try every size and wrapping above before falling back to another side.
        for (let side = 0; side < 4; side++) {
            for (const scale of LABEL_SCALES) {
                const size = builder.h * ACCESSORY_LABEL_SCALE * scale;
                for (const lines of variants) {
                    const box = builder.textBox(lines, size, { x: 0, y: 0 }, 0);
                    const gap = builder.h * (TEXT_GAP + (row - 1) * TEXT_ROW);
                    const candidates: { center: Vec2; edge: Vec2; near: Vec2 }[] = [
                        { center: { x: cx, y: rect.maxY + gap + box.maxY }, edge: { x: cx, y: rect.maxY }, near: { x: cx, y: rect.maxY + gap } },
                        { center: { x: cx, y: rect.minY - gap - box.maxY }, edge: { x: cx, y: rect.minY }, near: { x: cx, y: rect.minY - gap } },
                        { center: { x: rect.maxX + gap + box.maxX, y: cy }, edge: { x: rect.maxX, y: cy }, near: { x: rect.maxX + gap, y: cy } },
                        { center: { x: rect.minX - gap - box.maxX, y: cy }, edge: { x: rect.minX, y: cy }, near: { x: rect.minX - gap, y: cy } }
                    ];
                    const { center, edge, near } = candidates[side];
                    if (builder.isSegmentFree(edge, near) && builder.tryPlace(lines, size, [center], 0, accept)) {
                        builder.line(edge, near);
                        return;
                    }
                }
            }
        }
    }
}

function addUniversalPrefix(variants: string[][], isUniversal?: boolean): string[][] {
    return isUniversal ? variants.map(([first, ...rest]) => [`U ${first}`, ...rest]) : variants;
}

/**
 * Converts the scene into a collision free 2D drawing in display space. All decisions (text placement,
 * dimension tracks) are made after the display mapping, so they hold for every view mode and wall normal.
 */
export function layoutDimensionDrawing(
    scene: DimensionScene,
    mapping: DisplayMapping,
    { settings, groupColors, measureText }: LayoutOptions
): DimensionDrawing {
    const isOverlay = mapping.mode === 'overlay';
    const showLabels = settings.annotationMode !== 'chains';
    const showChains = settings.annotationMode !== 'labels';
    const dimensionUnit = settings.unit;

    const wallRect = scene.wallBounds ? mapRect(mapping, scene.wallBounds) : null;
    const wallOutline = scene.wallOutline.map((p) => mapPoint(mapping, p));
    const panels = scene.panels.map((p) => ({ ...p, display: mapRect(mapping, p.rect) }));
    const accessories = scene.accessories.map((a) => {
        const polygons = a.polygons.map((polygon) => polygon.map((ring) => ring.map((p) => mapPoint(mapping, p))));
        return { ...a, polygons, display: mapRect(mapping, a.bounds), center: mapPoint(mapping, a.center) };
    });
    // anchors may be part of the scene only for the anchor spacing chain
    const drawnAccessories = accessories.filter((a) => a.kind === 'anchor' ? settings.showAnchors : settings.showConnectors);

    let bounds = wallRect ?? emptyRect();
    panels.forEach((p) => (bounds = unionRect(bounds, p.display)));
    drawnAccessories.forEach((a) => (bounds = unionRect(bounds, a.display)));
    if (!isRectValid(bounds)) {
        return { fills: [], lines: [], dashedLines: [], texts: [] };
    }

    const referenceWidth = (wallRect ?? bounds).maxX - (wallRect ?? bounds).minX;
    const h = Math.min(MAX_TEXT_SIZE, Math.max(MIN_TEXT_SIZE, referenceWidth * TEXT_SIZE_RATIO));
    const builder = new DrawingBuilder(h, measureText);
    const { drawing } = builder;

    // --- fills & outlines -------------------------------------------------------------------------
    const panelOpacity = isOverlay ? 0.25 : 0.85;
    const accessoryOpacity = isOverlay ? 0.6 : 0.9;

    const groupRects = new Map<string, Rect>();
    for (const panel of panels) {
        if (panel.groupId && groupColors[panel.groupId]) {
            const current = groupRects.get(panel.groupId);
            groupRects.set(panel.groupId, current ? unionRect(current, panel.display) : panel.display);
        }
    }
    for (const [groupId, rect] of groupRects) {
        drawing.fills.push({ polygon: rectPolygon(rect), color: groupColors[groupId], opacity: 0.3 });
    }

    for (const panel of panels) {
        const groupColor = panel.groupId ? groupColors[panel.groupId] : undefined;
        const polygon = rectPolygon(panel.display);
        drawing.fills.push({ polygon, color: groupColor ?? PANEL_COLOR, opacity: panelOpacity });
        builder.outline(polygon, OUTLINE_COLOR);
    }

    const obstacles: Obstacle[] = [];
    for (const accessory of drawnAccessories) {
        const groupColor = accessory.groupId ? groupColors[accessory.groupId] : undefined;
        const color = groupColor ? darken(groupColor) : ACCESSORY_COLOR;
        for (const polygon of accessory.polygons) {
            drawing.fills.push({ polygon, color, opacity: accessoryOpacity });
            builder.outline(polygon, OUTLINE_COLOR);
            const ring = polygon[0] ?? [];
            obstacles.push({ ring, bounds: ring.reduce((r, p) => extendRect(r, p), emptyRect()) });
        }
    }

    for (let i = 0; i < wallOutline.length; i++) {
        drawing.dashedLines.push({ a: wallOutline[i], b: wallOutline[(i + 1) % wallOutline.length], color: DIMENSION_COLOR });
    }

    // --- dimension tracks -------------------------------------------------------------------------
    const reference = wallRect ?? bounds;
    // horizontal dimensions are placed on the side facing away from the floor fold (projection) or above the wall (overlay)
    const dirY: 1 | -1 = mapping.scaleY < 0 ? -1 : 1;
    const edgeY = dirY < 0 ? bounds.minY : bounds.maxY;
    const edgeX = bounds.minX;
    const toRealX = (d: number) => Math.abs(d);
    const toRealY = (d: number) => unmapLengthY(mapping, d);
    const plain = (v: number) => formatLength(v, dimensionUnit);

    const overallX = uniqueStops([reference.minX, reference.maxX]);
    const overallY = uniqueStops([reference.minY, reference.maxY]);

    // labels are placed before the chains: chain values may stagger around them, labels may not move
    if (showLabels) {
        for (const accessory of drawnAccessories) {
            // Marker bounds are placeholders, not actual accessory dimensions.
            if (accessory.source === 'marker') {
                continue;
            }
            placeAccessoryLabel(
                builder,
                addUniversalPrefix(
                    formatSizeVariants(
                        accessory.bounds.maxX - accessory.bounds.minX,
                        accessory.bounds.maxY - accessory.bounds.minY,
                        dimensionUnit
                    ),
                    accessory.isUniversal
                ),
                accessory.display,
                obstacles
            );
        }
        const sorted = [...panels].sort(
            (a, b) =>
                (b.rect.maxX - b.rect.minX) * (b.rect.maxY - b.rect.minY) -
                (a.rect.maxX - a.rect.minX) * (a.rect.maxY - a.rect.minY)
        );
        for (const panel of sorted) {
            const variants = addUniversalPrefix(
                formatSizeVariants(
                    panel.rect.maxX - panel.rect.minX,
                    panel.rect.maxY - panel.rect.minY,
                    dimensionUnit
                ),
                panel.isUniversal
            );
            placePanelLabel(builder, variants, panel.display, obstacles);
        }
    }

    if (showChains) {
        const horizontal: { stops: number[]; format: (v: number) => string }[] = [];
        if (settings.chains.panels) {
            const stops = uniqueStops([
                reference.minX,
                ...panels.flatMap((p) => [p.display.minX, p.display.maxX]),
                reference.maxX
            ]);
            if (!(settings.chains.overall && sameStops(stops, overallX))) {
                horizontal.push({ stops, format: plain });
            }
        }
        if (settings.showAnchors && settings.chains.anchors) {
            const anchors = accessories.filter((a) => a.kind === 'anchor');
            if (anchors.length > 0) {
                horizontal.push({
                    stops: uniqueStops([reference.minX, ...anchors.map((a) => a.center.x), reference.maxX]),
                    format: plain
                });
            }
        }
        if (settings.chains.overall) {
            horizontal.push({ stops: overallX, format: plain });
        }

        // each track starts behind the values of the previous one, so staggered values never collide
        let trackY = edgeY + dirY * FIRST_TRACK * h;
        for (const chain of horizontal.filter((c) => c.stops.length >= 2)) {
            const extent = addChain(builder, {
                axis: 'x',
                stops: chain.stops,
                track: trackY,
                edge: edgeY,
                dir: dirY,
                toReal: toRealX,
                format: chain.format
            });
            trackY += dirY * Math.max(TRACK_SPACING * h, extent + TRACK_CLEARANCE * h);
        }

        const vertical: { stops: number[]; format: (v: number) => string }[] = [];
        if (settings.chains.heights) {
            const stops = uniqueStops([reference.minY, reference.maxY, ...panels.flatMap((p) => [p.display.minY, p.display.maxY])]);
            if (!(settings.chains.overall && sameStops(stops, overallY))) {
                vertical.push({ stops, format: plain });
            }
        }
        if (settings.chains.overall) {
            vertical.push({ stops: overallY, format: plain });
        }

        let trackX = edgeX - FIRST_TRACK * h;
        for (const chain of vertical.filter((c) => c.stops.length >= 2)) {
            const extent = addChain(builder, {
                axis: 'y',
                stops: chain.stops,
                track: trackX,
                edge: edgeX,
                dir: -1,
                toReal: toRealY,
                format: chain.format
            });
            trackX -= Math.max(TRACK_SPACING * h, extent + TRACK_CLEARANCE * h);
        }
    } else if (overallX.length === 2 && overallY.length === 2) {
        // labels only: overall size as plain texts next to the wall (no dimension lines), shrunk before moved
        const centerX = (overallX[0] + overallX[1]) / 2;
        const centerY = (overallY[0] + overallY[1]) / 2;
        const distances = [1.2, 2.4, 3.6, 4.8].map((d) => d * h);
        const placeOverall = (text: string, rotation: 0 | 90, at: (distance: number) => Vec2) =>
            distances.some((d) => CHAIN_SCALES.some((scale) => builder.tryPlace([text], h * scale, [at(d)], rotation)));
        placeOverall(plain(toRealX(overallX[1] - overallX[0])), 0, (d) => ({ x: centerX, y: edgeY + dirY * d }));
        placeOverall(plain(toRealY(overallY[1] - overallY[0])), 90, (d) => ({ x: edgeX - d, y: centerY }));
    }

    return builder.finish(!isOverlay);
}
